/**
 * TaskDrawer Component
 *
 * Modal for creating, viewing, and editing tasks.
 * Accepts team members and patients via props instead of mock data.
 */

import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Timeline,
  Title,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
  IconCalendar,
  IconCheck,
  IconClock,
  IconEdit,
  IconNote,
  IconTrash,
  IconUser,
} from '@tabler/icons-react';
import type { Task, Reference } from '@medplum/fhirtypes';
import { isSystemManagedTask } from '@unify/fhir-model/resources/task';
import { useCallback, useEffect, useState } from 'react';
import type { TaskFormValues, TaskInboxStatus, TaskPriority } from '../types';
import {
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_FILTER_OPTIONS,
  TaskInboxStatusLabels,
  TaskPriorityColors,
  TaskPriorityLabels,
  TaskStatusColors,
  toInboxStatus,
} from '../types';
import type { TeamMember } from '../hooks/useTeamMembers';
import { usePatientSearch } from '../hooks/usePatientSearch';

type DrawerMode = 'view' | 'edit' | 'create';

interface TaskDrawerProps {
  opened: boolean;
  onClose: () => void;
  task?: Task | null;
  mode: DrawerMode;
  onSave: (task: Task, mode: 'create' | 'edit') => void;
  onDelete?: (task: Task) => void;
  teamMembers: TeamMember[];
}

const defaultFormValues: TaskFormValues = {
  description: '',
  status: 'requested',
  priority: 'routine',
  dueDate: null,
  assignee: null,
  patient: null,
  notes: '',
};

export function TaskDrawer({
  opened,
  onClose,
  task,
  mode: initialMode,
  onSave,
  onDelete,
  teamMembers,
}: TaskDrawerProps): JSX.Element {
  const [mode, setMode] = useState<DrawerMode>(initialMode);
  const [formValues, setFormValues] = useState<TaskFormValues>(defaultFormValues);
  const [saving, setSaving] = useState(false);

  const { patients } = usePatientSearch();

  useEffect(() => {
    setMode(initialMode);
    if (task && (initialMode === 'edit' || initialMode === 'view')) {
      setFormValues({
        description: task.description || '',
        status: toInboxStatus(task.status),
        priority: (task.priority as TaskPriority) || 'routine',
        dueDate: task.restriction?.period?.end || null,
        assignee: (task.owner as unknown as TaskFormValues['assignee']) || null,
        patient: (task.for as unknown as TaskFormValues['patient']) || null,
        notes: task.note?.[0]?.text || '',
      });
    } else {
      setFormValues(defaultFormValues);
    }
  }, [task, initialMode, opened]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!formValues.description.trim()) {
      showNotification({
        color: 'red',
        message: 'Task description is required',
      });
      return;
    }

    setSaving(true);

    try {
      const selectedAssignee = formValues.assignee
        ? teamMembers.find((member) => member.reference === formValues.assignee?.reference)
        : undefined;
      const savedTask: Task = {
        resourceType: 'Task',
        ...(task?.id && { id: task.id }),
        status: formValues.status,
        intent: 'order',
        priority: formValues.priority,
        description: formValues.description,
        authoredOn: task?.authoredOn || new Date().toISOString(),
        ...(formValues.dueDate && {
          restriction: {
            period: {
              end: formValues.dueDate,
            },
          },
        }),
        ...(formValues.assignee && {
          owner: {
            reference: formValues.assignee.reference,
            ...(selectedAssignee?.preferredName
              ? { display: selectedAssignee.preferredName }
              : {}),
          },
        }),
        ...(formValues.patient && {
          for: { reference: formValues.patient.reference, display: formValues.patient.display },
        }),
        ...(formValues.notes && {
          note: [{ text: formValues.notes }],
        }),
        ...(task?.code && { code: task.code }),
        ...(task?.requester && { requester: task.requester }),
        ...(task?.focus && { focus: task.focus }),
        ...(task?.meta && { meta: task.meta }),
        ...(task?.extension && { extension: task.extension }),
      };

      onSave(savedTask, mode === 'create' ? 'create' : 'edit');
      onClose();
    } catch {
      showNotification({
        color: 'red',
        message: 'Failed to save task. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  }, [formValues, task, mode, onSave, onClose, teamMembers]);

  const handleDelete = useCallback((): void => {
    if (task && onDelete) {
      onDelete(task);
      onClose();
    }
  }, [task, onDelete, onClose]);

  const isViewMode = mode === 'view';
  const systemManaged = Boolean(task && isSystemManagedTask(task));
  const notesDirty =
    !systemManaged &&
    isViewMode &&
    task &&
    formValues.notes !== (task.note?.[0]?.text || '');

  const assigneeOptions = teamMembers.map((member) => ({
    value: member.reference,
    label: member.name,
  }));

  const patientOptions = patients.map((patient) => ({
    value: patient.reference,
    label: patient.name,
  }));

  const statusOptions = TASK_STATUS_FILTER_OPTIONS
    .filter((option) => !systemManaged || option.value !== 'completed')
    .map((option) => ({
      value: option.value,
      label: option.label,
    }));

  const priorityOptions = TASK_PRIORITY_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Title order={4}>
            {mode === 'create' ? 'New Task' : mode === 'edit' ? 'Edit Task' : 'Task Details'}
          </Title>
          {isViewMode && (
            <ActionIcon variant="subtle" onClick={() => setMode('edit')} aria-label="Edit task">
              <IconEdit size={18} />
            </ActionIcon>
          )}
        </Group>
      }
      size="md"
      centered
    >
      <Stack gap="md">
        {/* Description */}
        {isViewMode ? (
          <Box>
            <Text size="xs" c="dimmed" mb={4}>
              Description
            </Text>
            <Text size="sm">{formValues.description || 'No description'}</Text>
          </Box>
        ) : (
          <Textarea
            label="Description"
            placeholder="What needs to be done?"
            value={formValues.description}
            onChange={(e) => setFormValues({ ...formValues, description: e.target.value })}
            minRows={2}
            maxRows={4}
            required
            autoFocus={mode === 'create'}
          />
        )}

        {/* Status & Priority */}
        <Group grow>
          {isViewMode ? (
            <>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>
                  Status
                </Text>
                <Text
                  size="sm"
                  c={TaskStatusColors[formValues.status]}
                  fw={500}
                >
                  {TaskInboxStatusLabels[formValues.status]}
                </Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>
                  Priority
                </Text>
                <Text
                  size="sm"
                  c={TaskPriorityColors[formValues.priority]}
                  fw={500}
                >
                  {TaskPriorityLabels[formValues.priority]}
                </Text>
              </Box>
            </>
          ) : (
            <>
              <Select
                label="Status"
                data={statusOptions}
                value={formValues.status}
                onChange={(value) =>
                  setFormValues({ ...formValues, status: value as TaskInboxStatus })
                }
              />
              <Select
                label="Priority"
                data={priorityOptions}
                value={formValues.priority}
                onChange={(value) =>
                  setFormValues({ ...formValues, priority: value as TaskPriority })
                }
              />
            </>
          )}
        </Group>

        {/* Due Date */}
        {isViewMode ? (
          <Box>
            <Text size="xs" c="dimmed" mb={4}>
              <Group gap={4}>
                <IconCalendar size={12} />
                Due Date
              </Group>
            </Text>
            <Text size="sm">
              {formValues.dueDate
                ? new Date(formValues.dueDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'No due date set'}
            </Text>
          </Box>
        ) : (
          <TextInput
            label="Due Date"
            type="date"
            leftSection={<IconCalendar size={16} />}
            value={formValues.dueDate || ''}
            onChange={(e) =>
              setFormValues({
                ...formValues,
                dueDate: e.target.value || null,
              })
            }
          />
        )}

        {/* Assignee */}
        {isViewMode ? (
          <Box>
            <Text size="xs" c="dimmed" mb={4}>
              <Group gap={4}>
                <IconUser size={12} />
                Assigned To
              </Group>
            </Text>
            <Text size="sm">{(formValues.assignee as Reference | null)?.display || 'Unassigned'}</Text>
          </Box>
        ) : (
          <Select
            label="Assign To"
            placeholder="Select assignee"
            leftSection={<IconUser size={16} />}
            data={assigneeOptions}
            value={(formValues.assignee as Reference | null)?.reference || null}
            onChange={(value) => {
              const member = teamMembers.find((m) => m.reference === value);
              setFormValues({
                ...formValues,
                assignee: member
                  ? {
                      reference: member.reference,
                      ...(member.preferredName ? { display: member.preferredName } : {}),
                    }
                  : null,
              });
            }}
            clearable
            searchable
          />
        )}

        {/* Client (related patient) */}
        {isViewMode ? (
          formValues.patient && (
            <Box>
              <Text size="xs" c="dimmed" mb={4}>
                Related Client
              </Text>
              <Text size="sm">{(formValues.patient as Reference | null)?.display}</Text>
            </Box>
          )
        ) : (
          <Select
            label="Related Client (Optional)"
            placeholder="Select client"
            data={patientOptions}
            value={(formValues.patient as Reference | null)?.reference || null}
            onChange={(value) => {
              const patient = patients.find((p) => p.reference === value);
              setFormValues({
                ...formValues,
                patient: patient
                  ? { reference: patient.reference, display: patient.name }
                  : null,
              });
            }}
            clearable
            searchable
          />
        )}

        {/* Notes — lifecycle-owned inventory tasks are not manually annotated. */}
        {!systemManaged && <Box>
          <Text size="xs" c="dimmed" mb={4}>
            <Group gap={4}>
              <IconNote size={12} />
              Notes
            </Group>
          </Text>
          <Textarea
            placeholder="Additional context or instructions..."
            value={formValues.notes}
            onChange={(e) => setFormValues({ ...formValues, notes: e.target.value })}
            minRows={3}
            maxRows={6}
          />
        </Box>}

        {/* Task metadata (view mode only) */}
        {isViewMode && task && (
          <>
            <Divider my="sm" />
            <Box>
              <Text size="xs" c="dimmed" mb="sm">
                Activity
              </Text>
              <Timeline active={0} bulletSize={20} lineWidth={2}>
                <Timeline.Item
                  bullet={<IconClock size={12} />}
                  title="Task Created"
                >
                  <Text size="xs" c="dimmed">
                    {task.authoredOn
                      ? new Date(task.authoredOn).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                      : 'Unknown'}
                  </Text>
                </Timeline.Item>
              </Timeline>
            </Box>
          </>
        )}

        {/* Actions */}
        <Divider my="sm" />
        <Group justify="space-between">
          {task && onDelete && !systemManaged && (
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
          <Group gap="sm" justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {isViewMode ? 'Close' : 'Cancel'}
            </Button>
            {isViewMode && task ? (
              <>
                {notesDirty && (
                  <Button
                    leftSection={<IconCheck size={16} />}
                    onClick={handleSave}
                    loading={saving}
                    disabled={saving}
                  >
                    Save notes
                  </Button>
                )}
                <Button
                  leftSection={<IconEdit size={16} />}
                  onClick={() => setMode('edit')}
                >
                  Edit
                </Button>
              </>
            ) : !isViewMode ? (
              <Button
                leftSection={<IconCheck size={16} />}
                onClick={handleSave}
                loading={saving}
                disabled={saving}
              >
                {mode === 'create' ? 'Create Task' : 'Save Changes'}
              </Button>
            ) : null}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
